import React, { useState } from 'react';
import { getEmploymentHistoryEntries, getExperienceSummary, getAppField } from '../utils/applicationFields';
import { getT, formatT } from '../i18n';

const EmploymentHistoryPanel = ({ app, lang = 'zh', defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const t = getT(lang).employment;
  const entries = getEmploymentHistoryEntries(app);
  const summary = getExperienceSummary(app);
  const hasContent = entries.length > 0 || Boolean(summary);
  const detailFields = [
    { label: t.currentlyEmployed, keys: ['is_currently_employed', 'isCurrentlyEmployed'] },
    { label: t.currentEmployer, keys: ['current_employer', 'currentEmployer'] },
    { label: t.currentRole, keys: ['current_role', 'currentRole'] },
    { label: t.employmentPeriod, keys: ['current_employment_period', 'currentEmploymentPeriod'] },
    { label: t.noticePeriod, keys: ['notice_period', 'noticePeriod'] },
    { label: t.outstandingOffer, keys: ['has_outstanding_job_offer', 'hasOutstandingJobOffer'] },
    { label: t.backgroundCheck, keys: ['comprehensive_background_check', 'comprehensiveBackgroundCheck'] },
    { label: t.drugFreeWorkplace, keys: ['drug_free_workplace', 'drugFreeWorkplace'] },
    { label: t.pastInfractions, keys: ['past_infractions_status', 'pastInfractionsStatus'] }
  ];

  const renderContent = () => {
    if (!hasContent) {
      return (
        <div className="employment-history-empty">
          {t.empty}
        </div>
      );
    }

    return (
      <div className="employment-history-panel">
        {summary ? (
          <div className="employment-history-summary">
            <span className="employment-history-summary-label">{t.totalExperience}</span>
            <span className="employment-history-summary-value">{summary}</span>
          </div>
        ) : null}

        <div className="employment-history-timeline">
          {entries.map((entry, index) => (
            <article key={`${entry.label}-${index}`} className="employment-history-card">
              <div className="employment-history-marker" aria-hidden="true">
                {index + 1}
              </div>
              <div className="employment-history-content">
                <h5 className="employment-history-title">{entry.label}</h5>

                <div className="employment-history-field">
                  <span className="employment-history-field-label">{t.companyRole}</span>
                  <p className="employment-history-field-value">{entry.company}</p>
                </div>

                {entry.reason ? (
                  <div className="employment-history-field">
                    <span className="employment-history-field-label">{t.reasonLeaving}</span>
                    <p className="employment-history-field-value">{entry.reason}</p>
                  </div>
                ) : null}

                {entry.clearance ? (
                  <div className="employment-history-field">
                    <span className="employment-history-field-label">{t.clearanceCoe}</span>
                    <p className="employment-history-field-value">{entry.clearance}</p>
                  </div>
                ) : null}
              </div>
            </article>
          ))}
        </div>

        <div className="employment-history-details-grid">
          {detailFields.map((field) => (
            <div key={field.label} className="employment-history-detail-item">
              <span className="employment-history-detail-label">{field.label}</span>
              <p className="employment-history-detail-value">{getAppField(app, field.keys)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="employment-history-accordion">
      <button
        type="button"
        className={`employment-history-toggle ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
      >
        <span className="employment-history-toggle-text">
          <span className="employment-history-toggle-label">{t.title}</span>
          {entries.length > 0 ? (
            <span className="employment-history-toggle-meta">
              {formatT(t.entryCount, { n: entries.length })}
            </span>
          ) : null}
        </span>
      </button>

      {isOpen ? (
        <div className="employment-history-collapse">
          {renderContent()}
        </div>
      ) : null}
    </div>
  );
};

export default EmploymentHistoryPanel;
